import React, { useState, useEffect } from 'react';
import './Forms.css';
import { useParams, useNavigate } from 'react-router-dom';
import { saveUser, getUsersByCompanyId } from '../services/UserServices';
import { getProjects } from '../services/ProjectServices';

function UserAdminPanel() {
    const { companyId, userId } = useParams();
    const navigate = useNavigate();
    const [userData, setUserData] = useState({
        firstName: '',
        lastName: '',
        email: '',
        password: '',
        projects: []
    });
    const [errors, setErrors] = useState({});
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [message, setMessage] = useState('');
    const [allProjects, setAllProjects] = useState([]);
    const isEditMode = !!userId;

    useEffect(() => {
        const fetchProjects = async () => {
            try {
                const response = await getProjects();
                const data = response.data || response;
                const filtered = data.filter(project => project.companyId === companyId);
                setAllProjects(filtered);
            } catch (error) {
                console.error('Error fetching projects:', error);
            }
        };
        fetchProjects();
    }, [companyId]);

    useEffect(() => {
        const fetchAll = async () => {
            try {
                const response = await getProjects();
                const data = response.data || response;
                const filtered = data.filter(project => project.companyId === companyId);
                setAllProjects(filtered);

                if (isEditMode) {
                    const userResponse = await getUsersByCompanyId(companyId);
                    const users = userResponse.data || userResponse;
                    const user = users.find(u => u.id === userId);

                    if (user) {
                        const projects = Array.isArray(user.projects)
                            ? user.projects.map(p => {
                                const full = filtered.find(fp => fp.id === p.id);
                                return { id: p.id, name: full?.name || '', slug: full?.slug || '' };
                            }) : [];

                        setUserData({
                            firstName: user.firstName,
                            lastName: user.lastName,
                            email: user.email,
                            password: '',
                            projects
                        });                        
                    }
                }
            } catch (error) {
                console.error('Error cargando proyectos o usuario:', error);
            }
        };
        fetchAll();
    }, [companyId, userId, isEditMode]);

    const validateForm = () => {
        const newErrors = {};

        if (!userData.firstName.trim()) {
            newErrors.firstName = 'El nombre es requerido';
        } else if (userData.firstName.length < 2) {
            newErrors.firstName = 'El nombre debe tener al menos 2 caracteres';
        }

        if (!userData.lastName.trim()) {
            newErrors.lastName = 'El apellido es requerido';
        } else if (userData.lastName.length < 2) {
            newErrors.lastName = 'El apellido debe tener al menos 2 caracteres';
        }

        if (!userData.email.trim()) {
            newErrors.email = 'El correo electrónico es requerido';
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(userData.email)) {
            newErrors.email = 'Ingrese un correo electrónico válido';
        }

        if (!isEditMode && !userData.password) {
            newErrors.password = 'La contraseña es requerida';
        } else if (userData.password && userData.password.length < 6) {
            newErrors.password = 'La contraseña debe tener al menos 6 caracteres';
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setUserData(prev => ({ ...prev, [name]: value }));

        if (errors[name]) {
            setErrors(prev => ({ ...prev, [name]: '' }));
        }
    };

    const handleProjectSelection = (projectId) => {
        const project = allProjects.find(p => p.id === projectId);

        setUserData(prev => {
            const alreadySelected = prev.projects.find(p => p.id === projectId);
            const updatedProjects = alreadySelected
                ? prev.projects.filter(p => p.id !== projectId)
                : [...prev.projects, { id: project.id, name: project.name, slug: project.slug }];

            return { ...prev, projects: updatedProjects };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        setLoading(true);
        setMessage('');

        const userToSave = {
            ...userData,
            companyId,
            projects: Array.isArray(userData.projects) ? userData.projects : [],
            ...(isEditMode && { id: userId }),
            ...(isEditMode && !userData.password && { password: undefined }),
        };

        try {
            await saveUser(userToSave);
            setMessage({
                text: `Usuario ${isEditMode ? 'actualizado' : 'creado'} correctamente`,
                type: 'success'
            });

            if (!isEditMode) {
                setUserData({
                    firstName: '',
                    lastName: '',
                    email: '',
                    password: '',
                    projects: []
                });
            }

            setTimeout(() => navigate(-1), 2000);
        } catch (error) {
            console.error('Error completo:', error);
            setMessage({
                text: `Error: ${error.response?.data?.message || error.message}`,
                type: 'error'
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="page-container">
            <div className="form-container">
                <h2>{isEditMode ? 'Editar Usuario' : 'Nuevo Usuario'}</h2>

                <form onSubmit={handleSubmit}>
                    <div className="form-group">
                        <label htmlFor="firstName">Nombre*</label>
                        <input
                            type="text"
                            id="firstName"
                            name="firstName"
                            placeholder="Ingrese el nombre"
                            value={userData.firstName}
                            onChange={handleChange}
                            className={errors.firstName ? 'input-error' : ''}
                        />
                        {errors.firstName && <span className="error-message">{errors.firstName}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="lastName">Apellido*</label>
                        <input
                            type="text"
                            id="lastName"
                            name="lastName"
                            placeholder="Ingrese el apellido"
                            value={userData.lastName}
                            onChange={handleChange}
                            className={errors.lastName ? 'input-error' : ''}
                        />
                        {errors.lastName && <span className="error-message">{errors.lastName}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="email">Correo electrónico*</label>
                        <input
                            type="email"
                            id="email"
                            name="email"
                            placeholder="ejemplo@dominio.com"
                            value={userData.email}
                            onChange={handleChange}
                            className={errors.email ? 'input-error' : ''}
                        />
                        {errors.email && <span className="error-message">{errors.email}</span>}
                    </div>

                    <div className="form-group">
                        <label htmlFor="password">
                            Contraseña{!isEditMode && '*'}
                            {isEditMode && <span className="optional-text"></span>}
                        </label>
                        <div className="password-input-container">
                            <input
                                type={showPassword ? "text" : "password"}
                                id="password"
                                name="password"
                                placeholder={isEditMode ? "" : "Mínimo 6 caracteres"}
                                value={userData.password}
                                onChange={handleChange}
                                className={errors.password ? 'input-error' : ''}
                            />
                            <button
                                type="button"
                                onClick={() => setShowPassword(!showPassword)}
                                className="btn-show-password"
                                tabIndex="-1"
                            >
                                {showPassword ? (
                                    <i className="icon-eye-off"></i>
                                ) : (
                                    <i className="icon-eye"></i>
                                )}
                            </button>
                        </div>
                        {errors.password && <span className="error-message">{errors.password}</span>}
                    </div>

                    <div className="form-group">
                        <label>Proyectos asignados</label>
                        {allProjects.length === 0 ? (
                            <p className="no-projects-message">No hay proyectos disponibles</p>
                        ) : (
                            <div className="project-checkboxes-container">
                                {allProjects.map(project => (
                                    <div key={project.id} className="checkbox-item">
                                        <input
                                            type="checkbox"
                                            id={`project-${project.id}`}
                                            checked={Array.isArray(userData.projects) && userData.projects.some(p => p.id === project.id)}
                                            onChange={() => handleProjectSelection(project.id)}
                                            className="custom-checkbox"
                                        />
                                        <label htmlFor={`project-${project.id}`} className="checkbox-label">
                                            {project.name}
                                        </label>
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>

                    <div className="form-actions">
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="btn-cancel"
                            disabled={loading}
                        >
                            Cancelar
                        </button>
                        <button
                            type="submit"
                            className="btn-submit"
                            disabled={loading}
                        >
                            {loading ? (
                                <>
                                    <i className="icon-spinner spinner"></i>
                                    {isEditMode ? ' Actualizando...' : ' Creando...'}
                                </>
                            ) : isEditMode ? 'Actualizar Usuario' : 'Crear Usuario'}
                        </button>
                    </div>

                    {message && (
                        <div className={`form-message ${message.type}`}>
                            {message.text}
                        </div>
                    )}
                </form>
            </div>
        </div>
    );
}

export default UserAdminPanel;